import  { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Radio,
  Table,
  Tag,
  Space,
  Modal,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Popconfirm,
  Badge,
  Alert,
  Checkbox,
  Popover,
  Divider,
} from 'antd';
import {
  UserAddOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  SafetyOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  KeyOutlined,
  ReloadOutlined,
  CalendarOutlined,
  LoginOutlined,
  AppstoreOutlined,
  ReadOutlined,
  FormOutlined,
  RollbackOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  DownOutlined,
  UpOutlined,
  LockFilled,
  UnlockFilled,
} from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './AdminUserManagement.css';
import './AdminDashboard.css';
import axiosConfig from '../../../../../config/axiosConfig';
import { useUserContext, canWrite as canWritePage, AVAILABLE_PAGES as FALLBACK_AVAILABLE_PAGES } from '../../../../../context/userContext';

const { Text } = Typography;
const { Option } = Select;

const PAGE_GROUP_LABELS = {
  core: 'Core',
  whatsapp: 'WhatsApp',
  billing: 'Billing',
  settings: 'Settings',
  automation: 'Automation',
  email: 'Email',
  developer: 'Developer',
  admin: 'Admin',
  mos_admin: 'MOS Admin',
};

const PAGE_ICON_MAP = {
  dashboard: 'Dashboard',
  contacts: 'Contacts',
  inbox: 'Inbox',
  campaigns: 'Campaigns',
  templates: 'Templates',
  wallet: 'Wallet',
  waba: 'WABA',
  bot_flows: 'Bot Flow Builder',
  ai_chatbot: 'AI Chatbot',
  knowledge_base: 'Knowledge Base',
  email_dashboard: 'Email Dashboard',
  email_campaigns: 'Email Campaigns',
  email_templates: 'Email Templates',
  email_settings: 'Email Settings',
  email_sender: 'Email Sender',
  api_keys: 'API Keys & Developer Tools',
  settings: 'Settings',
  admin: 'Admin',
  platform_plans: 'Platform Plans',
  topup_plans: 'Top-Up Plans',
  payment_management: 'Payments',
};

const SIDEBAR_BACKED_PAGE_KEYS = ['ai_chatbot', 'knowledge_base', 'bot_flows', 'api_keys'];

const buildAssignablePageCatalog = (contextPages) => {
  const hasContextPages = Array.isArray(contextPages) && contextPages.length > 0;
  const sourcePages = hasContextPages ? contextPages : FALLBACK_AVAILABLE_PAGES;
  const ensuredPages = hasContextPages
    ? FALLBACK_AVAILABLE_PAGES.filter(page => SIDEBAR_BACKED_PAGE_KEYS.includes(page.key))
    : [];
  const pageByKey = new Map();

  [...sourcePages, ...ensuredPages].forEach(page => {
    if (!page?.key || pageByKey.has(page.key)) return;
    pageByKey.set(page.key, {
      key: page.key,
      label: SIDEBAR_BACKED_PAGE_KEYS.includes(page.key)
        ? PAGE_ICON_MAP[page.key]
        : page.label || PAGE_ICON_MAP[page.key] || page.key,
      group: page.group || 'core',
    });
  });

  return Array.from(pageByKey.values());
};

// Human-friendly descriptions shown in the Role info popover
const ROLE_DESCRIPTIONS = {
  'ADMIN': {
    summary: 'Full system access with read & write permissions on every section.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Templates', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'Settings', perm: 'write' },
      { page: 'Admin', perm: 'write' },
      { page: 'Reports & Analytics', perm: 'write' },
      { page: 'WABA Configuration', perm: 'write' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Templates', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
      { page: 'Email Identity / Domain', perm: 'write' },
      { page: 'Email Preferences', perm: 'write' },
    ],
  },
  'Super Admin': {
    summary: 'Full system access with read & write permissions on every section.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Templates', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'Settings', perm: 'write' },
      { page: 'Admin', perm: 'write' },
      { page: 'Reports & Analytics', perm: 'write' },
      { page: 'WABA Configuration', perm: 'write' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Templates', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
      { page: 'Email Identity / Domain', perm: 'write' },
      { page: 'Email Preferences', perm: 'write' },
    ],
  },
  'Marketing Admin': {
    summary: 'Manages campaigns, templates, contacts, wallet and can view reports. Full email access.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Templates', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'Reports & Analytics', perm: 'read' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Templates', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
    ],
  },
  'Operations Admin': {
    summary: 'Handles day-to-day operations — campaigns, inbox, contacts, settings, wallet, WABA and email. View-only reports.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Settings', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'WABA Configuration', perm: 'write' },
      { page: 'Reports & Analytics', perm: 'read' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Templates', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
      { page: 'Email Identity / Domain', perm: 'write' },
      { page: 'Email Preferences', perm: 'write' },
    ],
  },
  'Finance Admin': {
    summary: 'Manages wallet & billing. Full access to reports and dashboard. Read-only settings. Can view email dashboard.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'Reports & Analytics', perm: 'write' },
      { page: 'Settings', perm: 'read' },
      { page: 'Email Dashboard', perm: 'read' },
    ],
  },
  'Support Admin': {
    summary: 'Handles customer conversations and contacts. Can view templates and email templates but cannot edit them.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Templates', perm: 'read' },
      { page: 'Email Templates', perm: 'read' },
    ],
  },
  'DIRECT_CUSTOMER': {
    summary: 'Limited access — can view the dashboard and inbox only.',
    capabilities: [
      { page: 'Dashboard', perm: 'read' },
      { page: 'Inbox', perm: 'read' },
    ],
  },
};

// Default pages + permissions to auto-select when a role is chosen
// Keys are role name strings (case-sensitive, matching API rolename)
const ROLE_DEFAULT_PAGES = {
  'ADMIN':             { pages: FALLBACK_AVAILABLE_PAGES.map(p => p.key), perms: Object.fromEntries(FALLBACK_AVAILABLE_PAGES.map(p => [p.key, 'write'])) },
  'Super Admin':       { pages: FALLBACK_AVAILABLE_PAGES.map(p => p.key), perms: Object.fromEntries(FALLBACK_AVAILABLE_PAGES.map(p => [p.key, 'write'])) },
  'Marketing Admin':   { pages: ['dashboard','campaigns','templates','contacts','email_dashboard','email_templates','email_campaigns'], perms: { dashboard:'read', campaigns:'write', templates:'write', contacts:'write', email_dashboard:'read', email_templates:'write', email_campaigns:'write' } },
  'Operations Admin':  { pages: ['dashboard','campaigns','templates','inbox','contacts','settings','waba','bot_flows','ai_chatbot','knowledge_base','email_dashboard','email_templates','email_campaigns','email_settings','email_sender'], perms: { dashboard:'read', campaigns:'write', templates:'write', inbox:'write', contacts:'write', settings:'write', waba:'write', bot_flows:'write', ai_chatbot:'write', knowledge_base:'write', email_dashboard:'read', email_templates:'write', email_campaigns:'write', email_settings:'write', email_sender:'write' } },
  'Finance Admin':     { pages: ['dashboard','wallet','payment_management'], perms: { dashboard:'read', wallet:'write', payment_management:'write' } },
  'Support Admin':     { pages: ['dashboard','inbox','contacts','templates','email_templates','knowledge_base'], perms: { dashboard:'read', inbox:'write', contacts:'read', templates:'read', email_templates:'read', knowledge_base:'read' } },
  'DIRECT_CUSTOMER':   { pages: ['dashboard','inbox'],                                            perms: { dashboard:'read', inbox:'read' } },
};

const AdminUserManagement = () => {
  const [form] = Form.useForm();
  const { userCtx, availablePages: contextAvailablePages, rolePermissionPresets = {} } = useUserContext();
  const AVAILABLE_PAGES = buildAssignablePageCatalog(contextAvailablePages);
  const pageLabelByKey = AVAILABLE_PAGES.reduce((acc, page) => {
    acc[page.key] = page.label;
    return acc;
  }, {});
  const pageGroups = AVAILABLE_PAGES.reduce((acc, page) => {
    const groupName = page.group || 'core';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(page);
    return acc;
  }, {});
  const getPageLabel = (pageKey) => pageLabelByKey[pageKey] || PAGE_ICON_MAP[pageKey] || pageKey;
  // If user has admin page in read-only mode, hide create/edit forms
  const adminCanWrite = !userCtx || !userCtx.pages || canWritePage(userCtx, 'admin');
  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const [contactMethod, setContactMethod] = useState('email');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [showPassword, setShowPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Controls whether the Advanced Access (page permissions) section is visible
  const [showAdvancedAccess, setShowAdvancedAccess] = useState(false);

  // Pages & permissions for the create form
  const [selectedPages, setSelectedPages] = useState([]);
  const [pagePermissions, setPagePermissions] = useState({});

  // Edit Permissions modal state
  const [editPermUser, setEditPermUser] = useState(null);
  const [editPermPages, setEditPermPages] = useState([]);
  const [editPermPerms, setEditPermPerms] = useState({});
  const [editPermRole, setEditPermRole] = useState(null);
  const [editPermLoading, setEditPermLoading] = useState(false);

  // Restore deactivated user modal state
  const [restoreModal, setRestoreModal] = useState({ visible: false, users: [], pendingFormData: null, loading: false });

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Role color mapping
  const roleColorMap = {
    'Super Admin': 'red',
    'Operations Admin': 'orange',
    'Support Admin': 'cyan',
    'Finance Admin': 'gold',
    'Marketing Admin': 'purple',
    'Admin': 'purple',
    'Manager': 'blue',
    'User': 'green'
  };

  // Fetch roles and admin users on component mount
  useEffect(() => {
    fetchRoles();
    fetchAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch roles from API
  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await axiosConfig.get('/admin/roles');
      if (response.data?.status && response.data?.statuscode === 200) {
        const roles = (Array.isArray(response.data?.entity) ? response.data.entity : [])
          .filter(role => role?.status === undefined || role?.status === null || role?.status === 0)
          .map(role => ({
            value: role.roleid || role.id,
            label: role.rolename || role.role_name || role.name,
            color: roleColorMap[role.rolename || role.role_name || role.name] || 'default'
          }))
          .filter(role => role.value && role.label);
        setRoleOptions(roles);
      } else {
        setRoleOptions([]);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Failed to fetch roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchAdminUsers = async (page = pagination.page, search = searchText) => {
    setTableLoading(true);
    try {
      const companyid = sessionStorage.getItem('customeruuid');
      const response = await axiosConfig.post('/admin/users', {
        page,
        pageSize: pagination.pageSize,
        company_type: 'ADMIN',
        search,
        companyid
      });
      
      if (response.data?.status && response.data?.statuscode === 200) {
        const users = response.data.entity.users.map(user => {
          // Normalize countrycode — API may return "91" or "+91"
          const cc = user.countrycode
            ? (user.countrycode.startsWith('+') ? user.countrycode : `+${user.countrycode}`)
            : null;
          return {
            id: user.companyid,
            name: user.companyname,
            email: user.emailid || null,
            mobile: cc && user.contactno ? `${cc}${user.contactno}` : null,
            countrycode: cc,
            contactno: user.contactno,
            role: user.roleid,
            rolename: user.rolename,
            // API returns "Active" / "Inactive" as strings
            status: user.status === 'Active' ? 'active' : 'inactive',
            createdAt: user.entrytime ? new Date(user.entrytime).toLocaleDateString() : 'N/A',
            lastLogin: (user.lastlogintime || user.last_login || user.lastlogin || user.entrytime)
              ? new Date(user.lastlogintime || user.last_login || user.lastlogin || user.entrytime).toLocaleDateString()
              : 'Never',
            company_type: user.company_type,
            colour_theme: user.colour_theme,
            display_mode: user.display_mode,
            pages: user.pages || null,
            permissions: user.permissions || null,
            parent_company_id: user.parent_company_id || null
          };
        });
        setAdminUsers(users);
        setPagination({
          page: response.data.entity.pagination.page,
          pageSize: response.data.entity.pagination.pageSize,
          total: response.data.entity.pagination.total
        });
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      if (error.response?.status === 403) {
        message.error('Access denied: Super Admin privileges required');
      } else {
        message.error(error.response?.data?.message || 'Failed to fetch admin users');
      }
    } finally {
      setTableLoading(false);
    }
  };

  // Generate random password
  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one of each required character type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
    
    // Fill the rest
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Handle contact method change
  const handleContactMethodChange = (e) => {
    const method = e.target.value;
    setContactMethod(method);
    
    if (method === 'email') {
      form.setFieldsValue({ mobile: undefined, countrycode: undefined });
      setPhoneNumber('');
    } else {
      form.setFieldsValue({ email: undefined });
    }
  };

  // Handle phone change
  const handlePhoneChange = (value, country) => {
    const dialCode = country.dialCode;
    setPhoneNumber(value);
    const code = `+${dialCode}`;
    setCountryCode(code);
    form.setFieldsValue({
      mobile: value.replace(code.replace('+', ''), ''),
      countrycode: code,
    });
  };

  // Generate random password
  const handleGeneratePassword = () => {
    const generated = generatePassword();
    form.setFieldsValue({ password: generated, confirmPassword: generated });
    setShowPassword(true);
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    setLoading(true);
    // Hoist requestBody so catch block can access it for restore modal
    let requestBody = null;
    
    try {
      const userPassword = values.password;
      
      // Prepare request body according to API spec
      const parentId = sessionStorage.getItem('customeruuid') || null;
      requestBody = {
        companyname: values.name,
        password: userPassword,
        company_type: 'ADMIN',
        parent_partner_id: parentId,
        colour_theme: 'light',
        display_mode: 'default',
        roleid: values.role,
        pages: selectedPages.length > 0 ? selectedPages : null,
        permissions: Object.keys(pagePermissions).length > 0 ? pagePermissions : null
      };
      
      // Add email or mobile based on contact method
      if (contactMethod === 'email') {
        requestBody.emailid = values.email;
        // Do NOT include contactno/countrycode at all for email-only
        delete requestBody.countrycode;
        delete requestBody.contactno;
      } else {
        requestBody.emailid = '';
        requestBody.countrycode = countryCode;
        requestBody.contactno = values.mobile;
      }

      const response = await axiosConfig.post('/admin/create-user', requestBody);
      
      if (response.data?.status && response.data?.statuscode === 201) {
        const createdUser = response.data.entity;
        const username = contactMethod === 'email' ? createdUser.emailid : `${createdUser.countrycode}${createdUser.contactno}`;
        
        setCreatedCredentials({
          name: createdUser.companyname,
          email: createdUser.emailid || null,
          mobile: createdUser.contactno ? `${createdUser.countrycode}${createdUser.contactno}` : null,
          role: values.role,
          rolename: createdUser.rolename || createdUser.company_type || 'Unknown',
          password: userPassword,
          username: username,
          pages: createdUser.pages || selectedPages,
          permissions: createdUser.permissions || pagePermissions
        });
        
        setShowCredentialsModal(true);
        setShowCreateModal(false);
        form.resetFields();
        setPhoneNumber('');
        setCountryCode('+91');
        setContactMethod('email');
        setShowPassword(false);
        setSelectedPages([]);
        setPagePermissions({});
        message.success(response.data?.message || 'Admin user created successfully!');
        
        // Refresh user list
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to create admin user');
      }
    } catch (error) {
      console.error('Error creating admin user:', error);
      const status = error.response?.status;
      const errData = error.response?.data;

      // ── 409: Deactivated user with same credentials ───────────────────
      if (status === 409 && errData?.code === 'DEACTIVATED_USER_EXISTS') {
        setRestoreModal({
          visible: true,
          users: errData.deactivated_users || [],
          pendingFormData: requestBody,  // store full payload for restore
          loading: false,
        });
        return; // don't show generic error
      }

      let errorMessage = errData?.message || 'Failed to create admin user';
      if (status === 400) {
        if (errorMessage.includes('email')) errorMessage = 'Email already registered';
        else if (errorMessage.includes('mobile') || errorMessage.includes('contact')) errorMessage = 'Mobile number already registered';
      } else if (status === 403) {
        errorMessage = 'Access denied: Super Admin privileges required';
      }
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ── Restore a previously deactivated user ───────────────────────────────
  const handleRestore = async () => {
    const { users, pendingFormData } = restoreModal;
    if (!users.length || !pendingFormData) return;
    const targetUser = users[0];
    setRestoreModal(prev => ({ ...prev, loading: true }));
    try {
      const response = await axiosConfig.post(`/admin/users/${targetUser.companyid}/restore`, {
        companyname: pendingFormData.companyname,
        password: pendingFormData.password,
        company_type: pendingFormData.company_type,
        roleid: pendingFormData.roleid,
        pages: pendingFormData.pages,
        permissions: pendingFormData.permissions,
      });
      if (response.data?.status && (response.data?.statuscode === 200 || response.data?.statuscode === 201)) {
        const restoredUser = response.data.entity || {};
        // Show credentials modal with restored user info
        const username = pendingFormData.emailid || `${pendingFormData.countrycode || ''}${pendingFormData.contactno || ''}`;
        setCreatedCredentials({
          name: restoredUser.companyname || pendingFormData.companyname,
          email: restoredUser.emailid || pendingFormData.emailid || null,
          mobile: restoredUser.contactno ? `${restoredUser.countrycode || pendingFormData.countrycode || ''}${restoredUser.contactno}` : null,
          role: pendingFormData.roleid,
          rolename: restoredUser.rolename || restoredUser.company_type || pendingFormData.company_type || 'Admin',
          password: pendingFormData.password,
          username,
          pages: restoredUser.pages || pendingFormData.pages,
          permissions: restoredUser.permissions || pendingFormData.permissions,
          restored: true,
        });
        setRestoreModal({ visible: false, users: [], pendingFormData: null, loading: false });
        setShowCreateModal(false);
        form.resetFields();
        setPhoneNumber(''); setCountryCode('+91'); setContactMethod('email'); setShowPassword(false);
        setSelectedPages([]); setPagePermissions({});
        setShowCredentialsModal(true);
        message.success(response.data?.message || 'User restored successfully!');
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to restore user');
        setRestoreModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to restore user');
      setRestoreModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard!`);
  };

  // Handle status toggle
  const handleStatusToggle = async (record) => {
    try {
      let response;
      if (record.status === 'active') {
        // Deactivate user
        response = await axiosConfig.delete(`/admin/users/${record.id}`);
        if (response.data?.status && response.data?.statuscode === 200) {
          message.success(response.data?.message || 'User deactivated successfully');
        }
      } else {
        // Reactivate user
        response = await axiosConfig.post(`/admin/users/${record.id}/reactivate`);
        if (response.data?.status && response.data?.statuscode === 200) {
          message.success(response.data?.message || 'User reactivated successfully');
        }
      }
      fetchAdminUsers();
    } catch (error) {
      console.error('Error updating status:', error);
      const status = error.response?.status;
      let errorMessage = error.response?.data?.message || 'Failed to update user status';
      
      if (status === 403) {
        errorMessage = 'Access denied: Super Admin privileges required';
      } else if (status === 404) {
        errorMessage = 'User not found';
      }
      
      message.error(errorMessage);
    }
  };

  // Handle delete (permanent deactivation)
  const handleDelete = async (record) => {
    try {
      const response = await axiosConfig.delete(`/admin/users/${record.id}`);
      if (response.data?.status && response.data?.statuscode === 200) {
        message.success(response.data?.message || 'User deactivated successfully');
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const status = error.response?.status;
      let errorMessage = error.response?.data?.message || 'Failed to delete user';
      
      if (status === 403) {
        errorMessage = 'Access denied: Super Admin privileges required';
      } else if (status === 404) {
        errorMessage = 'User not found';
      }
      
      message.error(errorMessage);
    }
  };

  // Handle role update
  const handleRoleUpdate = async (userId, newRoleId) => {
    try {
      const response = await axiosConfig.put(`/admin/users/${userId}/role`, {
        roleid: newRoleId
      });
      
      if (response.data?.status && response.data?.statuscode === 200) {
        message.success(response.data?.message || 'Role updated successfully');
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      const status = error.response?.status;
      let errorMessage = error.response?.data?.message || 'Failed to update role';
      
      if (status === 403) {
        errorMessage = 'Access denied: Super Admin privileges required';
      } else if (status === 404) {
        errorMessage = 'User not found';
      }
      
      message.error(errorMessage);
    }
  };

  // Open Edit Permissions modal for a user
  const openEditPermissions = (record) => {
    setEditPermUser(record);
    setEditPermPages(record.pages || []);
    setEditPermPerms(record.permissions || {});
    setEditPermRole(record.role);
  };

  // Save updated permissions via PUT /admin/update-permissions/:companyid
  const handleSavePermissions = async () => {
    if (!editPermUser) return;
    setEditPermLoading(true);
    try {
      const response = await axiosConfig.put(`/admin/update-permissions/${editPermUser.id}`, {
        pages: editPermPages.length > 0 ? editPermPages : null,
        permissions: Object.keys(editPermPerms).length > 0 ? editPermPerms : null,
        roleid: editPermRole
      });
      if (response.data?.status && response.data?.statuscode === 200) {
        message.success(response.data?.message || 'Permissions updated successfully');
        setEditPermUser(null);
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      message.error(error.response?.data?.message || 'Failed to update permissions');
    } finally {
      setEditPermLoading(false);
    }
  };

  // Open / close Create Admin User modal
  const handleOpenCreateModal = () => {
    form.resetFields();
    setPhoneNumber('');
    setCountryCode('+91');
    setContactMethod('email');
    setShowPassword(false);
    setSelectedPages([]);
    setPagePermissions({});
    setShowAdvancedAccess(false);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    form.resetFields();
    setPhoneNumber('');
    setCountryCode('+91');
    setContactMethod('email');
    setShowPassword(false);
    setSelectedPages([]);
    setPagePermissions({});
    setShowAdvancedAccess(false);
  };

  const findPresetForRole = (roleName) => {
    if (!roleName || !rolePermissionPresets || typeof rolePermissionPresets !== 'object') return null;
    return rolePermissionPresets[roleName]
      || Object.entries(rolePermissionPresets).find(([key]) => key.toLowerCase() === roleName.toLowerCase())?.[1]
      || null;
  };

  const normalizeRoleDefaults = (defaults) => {
    if (!defaults) return null;
    const availableKeys = new Set(AVAILABLE_PAGES.map(page => page.key));
    const rawPermissions = defaults.permissions || defaults.perms || {};
    const rawPages = Array.isArray(defaults.pages) && defaults.pages.length > 0
      ? defaults.pages
      : Object.keys(rawPermissions);
    const pages = rawPages.filter(pageKey => availableKeys.has(pageKey));
    const perms = {};
    pages.forEach(pageKey => {
      perms[pageKey] = rawPermissions[pageKey] || 'read';
    });
    return { pages, perms };
  };

  const getRoleDefaults = (roleName) => {
    if (!roleName) return null;

    const fullAccessRoles = ['admin', 'super admin'];
    if (fullAccessRoles.includes(roleName.toLowerCase())) {
      const allKeys = AVAILABLE_PAGES.map(page => page.key);
      return { pages: allKeys, perms: Object.fromEntries(allKeys.map(pageKey => [pageKey, 'write'])) };
    }

    const backendDefaults = normalizeRoleDefaults(findPresetForRole(roleName));
    if (backendDefaults) return backendDefaults;

    const matchedKey = Object.keys(ROLE_DEFAULT_PAGES).find(key => key.toLowerCase() === roleName.toLowerCase());
    return normalizeRoleDefaults(matchedKey ? ROLE_DEFAULT_PAGES[matchedKey] : null);
  };

  // Auto-populate pages & permissions when a role is selected
  const handleRoleChange = (roleId) => {
    const roleName = roleOptions.find(r => r.value === roleId)?.label || '';
    const defaults = getRoleDefaults(roleName);
    if (defaults) {
      setSelectedPages(defaults.pages);
      setPagePermissions({ ...defaults.perms });
    }
  };

  const handleEditPermRoleChange = (roleId) => {
    setEditPermRole(roleId);
    const roleName = roleOptions.find(r => r.value === roleId)?.label || '';
    const defaults = getRoleDefaults(roleName);
    if (defaults) {
      setEditPermPages(defaults.pages);
      setEditPermPerms({ ...defaults.perms });
    }
  };

  // Validation for name (letters and spaces only, capitalize)
  const handleNameInput = (e) => {
    let value = e.target.value;
    // Remove non-letter characters except spaces
    value = value.replace(/[^a-zA-Z\s]/g, '');
    // Capitalize first letter of each word
    value = value.replace(/\b\w/g, char => char.toUpperCase());
    form.setFieldsValue({ name: value });
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            flexShrink: 0,
            background: `linear-gradient(135deg, ${roleOptions.find(r => r.value === record.role)?.color || '#1890ff'} 0%, #1890ff 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '12px'
          }}>
            {text?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{text}</div>
            <div style={{ fontSize: '11px', color: '#aaa', lineHeight: 1.2 }}>
              {record.createdAt}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
              <MailOutlined style={{ color: '#1890ff', fontSize: 12 }} />
              <span style={{ fontSize: '12px' }}>{record.email}</span>
            </div>
          )}
          {record.mobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <PhoneOutlined style={{ color: '#52c41a', fontSize: 12 }} />
              <span style={{ fontSize: '12px' }}>{record.mobile}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'rolename',
      key: 'role',
      render: (rolename, record) => {
        return (
          <Select
            value={record.role}
            onChange={(newRoleId) => handleRoleUpdate(record.id, newRoleId)}
            style={{ width: 150 }}
            size="small"
          >
            {roleOptions.map(role => (
              <Option key={role.value} value={role.value}>
                {role.label}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Pages',
      dataIndex: 'pages',
      key: 'pages',
      width: 180,
      render: (pages) => {
        if (!pages || pages.length === 0) {
          return <Tag style={{ margin: 0, borderRadius: 6, fontSize: 11, borderColor: '#e2e8f0', color: '#475569', background: '#f8fafc' }}>All Access</Tag>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {pages.slice(0, 3).map(p => (
              <Tag key={p} style={{ fontSize: 10, margin: 0, lineHeight: '18px', padding: '0 5px', borderRadius: 5, borderColor: '#e2e8f0', color: '#334155', background: '#f8fafc' }}>{getPageLabel(p)}</Tag>
            ))}
            {pages.length > 3 && (
              <Tooltip title={pages.slice(3).map(getPageLabel).join(', ')}>
                <Tag style={{ fontSize: 10, margin: 0, lineHeight: '18px', padding: '0 5px', borderRadius: 5, borderColor: '#dbeafe', color: '#1d4ed8', background: '#eff6ff' }}>+{pages.length - 3}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge
          status={status === 'active' ? 'success' : 'error'}
          text={
            <span style={{ 
              color: status === 'active' ? '#52c41a' : '#ff4d4f',
              fontWeight: 500 
            }}>
              {status === 'active' ? 'Active' : 'Inactive'}
            </span>
          }
        />
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (text) => (
        <span style={{ fontSize: '13px', color: '#666' }}>
          {text || 'Never'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: windowWidth >= 768 ? false : 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                const permissionIcon = (perm) => perm === 'write'
                  ? <FormOutlined style={{ color: '#1890ff', fontSize: '13px' }} />
                  : <ReadOutlined style={{ color: '#8c8c8c', fontSize: '13px' }} />;
                const permissionColor = (perm) => perm === 'write' ? '#e6f4ff' : '#fafafa';
                const permissionBorder = (perm) => perm === 'write' ? '#91caff' : '#e8e8e8';
                const permissionTagColor = (perm) => perm === 'write' ? 'blue' : 'default';

                Modal.info({
                  title: null,
                  icon: null,
                  width: windowWidth < 576 ? '92%' : windowWidth < 992 ? 600 : 700,
                  bodyStyle: { overflow: 'visible', padding: windowWidth < 576 ? '12px 14px 6px' : '14px 18px 8px' },
                  wrapClassName: 'admin-user-details-modal',
                  style: { top: windowWidth < 576 ? 18 : 30 },
                  content: (
                    <div style={{ marginTop: 0 }}>
                      {/* Header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '14px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <div style={{
                          width: windowWidth < 576 ? '40px' : '46px',
                          height: windowWidth < 576 ? '40px' : '46px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${roleOptions.find(r => r.value === record.role)?.color || '#1890ff'} 0%, #1890ff 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: windowWidth < 576 ? '15px' : '17px',
                          flexShrink: 0
                        }}>
                          {record.name?.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: windowWidth < 576 ? '15px' : '16px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>
                            {record.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                            <Tag color={roleColorMap[record.rolename] || 'default'} style={{ margin: 0, fontSize: '12px' }}>
                              {record.rolename || 'N/A'}
                            </Tag>
                            <Badge
                              status={record.status === 'active' ? 'success' : 'error'}
                              text={<span style={{ fontSize: '12px', color: record.status === 'active' ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
                                {record.status === 'active' ? 'Active' : 'Inactive'}
                              </span>}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact & Info Grid */}
                      <Row gutter={[8, 8]} style={{ marginBottom: '12px' }}>
                        <Col span={windowWidth < 576 ? 24 : 12}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                              <MailOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                              <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</Text>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a1a', wordBreak: 'break-all' }}>
                              {record.email || 'N/A'}
                            </div>
                          </div>
                        </Col>
                        <Col span={windowWidth < 576 ? 24 : 12}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                              <PhoneOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                              <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mobile</Text>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a1a' }}>
                              {record.mobile || 'N/A'}
                            </div>
                          </div>
                        </Col>
                        <Col span={windowWidth < 576 ? 24 : 12}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                              <CalendarOutlined style={{ color: '#faad14', fontSize: '12px' }} />
                              <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Created</Text>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a1a' }}>
                              {record.createdAt}
                            </div>
                          </div>
                        </Col>
                        <Col span={windowWidth < 576 ? 24 : 12}>
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                              <LoginOutlined style={{ color: '#722ed1', fontSize: '12px' }} />
                              <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Login</Text>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a1a' }}>
                              {record.lastLogin || 'Never'}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {/* Page Permissions Section */}
                      <div style={{ marginBottom: '0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <AppstoreOutlined style={{ color: '#1890ff', fontSize: '15px' }} />
                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>Page Access & Permissions</span>
                        </div>

                        {(!record.pages || record.pages.length === 0) ? (
                          <div style={{
                            background: 'linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)',
                            border: '1px solid #ffe58f',
                            borderRadius: '10px',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <SafetyOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '13px', color: '#ad6800' }}>Full Access (Super Admin)</div>
                              <div style={{ fontSize: '12px', color: '#ad8b00' }}>This user has unrestricted access to all pages</div>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            border: '1px solid #e8e8e8',
                            borderRadius: '10px',
                            overflow: 'hidden'
                          }}>
                            {/* Table Header */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: windowWidth < 576 ? '1fr' : '1fr 1fr',
                              gap: '1px',
                              background: '#fafafa',
                              padding: '8px 14px',
                              borderBottom: '1px solid #e8e8e8',
                            }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Page &amp; Permission</span>
                              {windowWidth >= 576 && <span style={{ fontSize: '11px', fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Page &amp; Permission</span>}
                            </div>
                            {/* Page Rows — 2-column grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: windowWidth < 576 ? '1fr' : '1fr 1fr', gap: '1px', background: '#f0f0f0' }}>
                              {record.pages.map((pageKey) => {
                                const perm = record.permissions?.[pageKey] || 'read';
                                const pageLabel = getPageLabel(pageKey);
                                return (
                                  <div
                                    key={pageKey}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '9px 12px',
                                      background: permissionColor(perm),
                                      gap: '6px',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                      {permissionIcon(perm)}
                                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pageLabel}</span>
                                    </div>
                                    <Tag
                                      color={permissionTagColor(perm)}
                                      style={{
                                        margin: 0,
                                        fontSize: '10px',
                                        borderRadius: '4px',
                                        border: `1px solid ${permissionBorder(perm)}`,
                                        padding: '0 5px',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {perm === 'write' ? 'R/W' : 'Read'}
                                    </Tag>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                  okText: 'Close',
                  okButtonProps: { style: { borderRadius: '6px' } },
                });
              }}
            />
          </Tooltip>
          {adminCanWrite && record.parent_company_id && (
            <Tooltip title="Edit Permissions">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                style={{ color: '#1890ff' }}
                onClick={() => openEditPermissions(record)}
              />
            </Tooltip>
          )}
          {adminCanWrite && (
          <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
            <Button
              type="text"
              icon={record.status === 'active' ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              size="small"
              style={{ color: record.status === 'active' ? '#ff4d4f' : '#52c41a' }}
              onClick={() => handleStatusToggle(record)}
            />
          </Tooltip>
          )}
          {adminCanWrite && (
          <Popconfirm
            title="Delete Admin User"
            description="Are you sure you want to delete this admin user?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
              />
            </Tooltip>
          </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0' }}>

      {/* ── Admin Users Table — full width ── */}
      <div className="admin-table-wrapper">
        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid #e8ecf1',
          display: 'flex',
          flexDirection: windowWidth < 576 ? 'column' : 'row',
          alignItems: windowWidth < 576 ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 7, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TeamOutlined style={{ color: '#8b5cf6', fontSize: 14 }} />
            </div>
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1d26' }}>Admin Users</span>
              <Badge count={pagination.total} style={{ backgroundColor: '#8b5cf6', marginLeft: 7 }} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: windowWidth < 576 ? 'column' : 'row', gap: 8, alignItems: windowWidth < 576 ? 'stretch' : 'center' }}>
            <Input.Search
              placeholder="Search users..."
              allowClear
              style={{ width: windowWidth < 576 ? '100%' : windowWidth < 768 ? 180 : 240, borderRadius: 8 }}
              onSearch={(value) => {
                setSearchText(value);
                setPagination(prev => ({ ...prev, page: 1 }));
                fetchAdminUsers(1, value);
              }}
            />
            <Space direction="horizontal" style={{ width: windowWidth < 576 ? '100%' : 'auto' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchAdminUsers()}
                loading={tableLoading}
                size={windowWidth < 576 ? 'small' : 'middle'}
                className="admin-btn-secondary"
                style={{ flex: windowWidth < 576 ? 1 : 'none' }}
              >
                Refresh
              </Button>
              {adminCanWrite && (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={handleOpenCreateModal}
                  size={windowWidth < 576 ? 'small' : 'middle'}
                  className="admin-btn-primary"
                  style={{
                    flex: windowWidth < 576 ? 1 : 'none',
                    padding: windowWidth < 576 ? '0 10px' : undefined,
                    fontSize: windowWidth < 576 ? 12 : undefined,
                  }}
                >
                  Create Admin
                </Button>
              )}
            </Space>
          </div>
        </div>
            <Table
              dataSource={adminUsers}
              columns={columns}
              loading={tableLoading}
              rowKey="id"
              size="small"
              scroll={{ x: 1000 }}
              pagination={{
                current: pagination.page,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: windowWidth >= 768,
                showTotal: (total, range) => windowWidth >= 576 ? `${range[0]}-${range[1]} of ${total} users` : `${total} total`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, page, pageSize }));
                  fetchAdminUsers(page, searchText);
                },
                simple: windowWidth < 576
              }}
              style={{ overflow: 'hidden' }}
          />
      </div>

      {/* Create Admin User Modal */}
      {adminCanWrite && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <UserAddOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>Create Admin User</span>
            </div>
          }
          open={showCreateModal}
          onCancel={handleCloseCreateModal}
          footer={null}
          width={windowWidth < 576 ? '98%' : windowWidth < 768 ? '95%' : windowWidth < 1200 ? 860 : 960}
          bodyStyle={{ overflow: 'visible', padding: '12px 24px 8px' }}
          wrapClassName="admin-user-details-modal"
          style={{ top: 24 }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off" style={{ '--ant-form-item-margin-bottom': '12px' }}>
            <Row gutter={[24, 0]}>
              {/* ── Left column: identity + credentials ── */}
              <Col xs={24} md={12}>
                {/* Name */}
                <Form.Item
                  name="name"
                  label="Full Name"
                  style={{ marginBottom: 12 }}
                  rules={[
                    { required: true, message: 'Please enter full name' },
                    { min: 3, message: 'Name must be at least 3 characters' },
                    { max: 50, message: 'Name cannot exceed 50 characters' },
                    { pattern: /^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/, message: 'Name must contain only letters and spaces' }
                  ]}
                >
                  <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter Full Name" size="large" onChange={handleNameInput} maxLength={50} />
                </Form.Item>

                {/* Contact Method */}
                <Form.Item label="Contact Method" style={{ marginBottom: 8 }}>
                  <Radio.Group onChange={handleContactMethodChange} value={contactMethod}>
                    <Radio value="email">Email</Radio>
                    <Radio value="mobile">Mobile</Radio>
                  </Radio.Group>
                </Form.Item>

                {/* Email / Mobile */}
                {contactMethod === 'email' ? (
                  <Form.Item
                    name="email"
                    label="Email Address"
                    style={{ marginBottom: 12 }}
                    rules={[
                      { required: true, message: 'Please enter email address' },
                      { pattern: /^[a-zA-Z0-9._%-+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter Email Address" size="large" maxLength={100} />
                  </Form.Item>
                ) : (
                  <Form.Item label="Mobile Number" required style={{ marginBottom: 12 }}>
                    <PhoneInput
                      country={'in'}
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      inputStyle={{ width: '100%', height: '40px', fontSize: '14px', borderRadius: '6px', paddingLeft: '52px', border: '1px solid #d9d9d9' }}
                      buttonStyle={{ border: '1px solid #d9d9d9', borderRight: 'none', borderRadius: '6px 0 0 6px', backgroundColor: '#fafafa', width: '44px' }}
                      dropdownStyle={{ width: '320px', maxHeight: '260px', overflowY: 'auto', zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderRadius: '8px' }}
                      searchStyle={{ width: '100%', padding: '6px 10px', fontSize: '13px' }}
                      containerStyle={{ width: '100%', position: 'relative' }}
                      enableSearch={true}
                      searchPlaceholder="Search country..."
                      countryCodeEditable={false}
                      preferredCountries={['in', 'us', 'gb', 'ae', 'sg']}
                    />
                  </Form.Item>
                )}
                <Form.Item name="mobile" hidden />
                <Form.Item name="countrycode" hidden initialValue="+91" />

                {/* Password */}
                <Form.Item
                  name="password"
                  style={{ marginBottom: 12 }}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>Password</span>
                      <Button type="link" size="small" onClick={handleGeneratePassword} style={{ padding: 0, height: 'auto', fontSize: '12px' }}>Generate Strong Password</Button>
                    </div>
                  }
                  rules={[
                    { required: true, message: 'Please enter a password' },
                    { min: 8, message: 'Password must be at least 8 characters' },
                    { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/, message: 'Must have uppercase, lowercase, number & special character' }
                  ]}
                >
                  <Input.Password prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter Password" size="large" visibilityToggle={{ visible: showPassword, onVisibleChange: setShowPassword }} />
                </Form.Item>

                {/* Confirm Password */}
                <Form.Item
                  name="confirmPassword"
                  label="Confirm Password"
                  style={{ marginBottom: 0 }}
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Please confirm the password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) return Promise.resolve();
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} placeholder="Confirm Password" size="large" />
                </Form.Item>
              </Col>

              {/* ── Right column: role + simplified access ── */}
              <Col xs={24} md={12}>
                {/* Role selector with info popover */}
                <Form.Item
                  name="role"
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Admin Role
                      <Form.Item noStyle shouldUpdate={(p, c) => p.role !== c.role}>
                        {({ getFieldValue }) => {
                          const roleId = getFieldValue('role');
                          const roleInfo = roleOptions.find(r => r.value === roleId);
                          const descKey = roleInfo ? Object.keys(ROLE_DESCRIPTIONS).find(k => k.toLowerCase() === roleInfo.label.toLowerCase()) : null;
                          const desc = descKey ? ROLE_DESCRIPTIONS[descKey] : null;
                          if (!desc) return null;
                          const popContent = (
                            <div style={{ maxWidth: 280 }}>
                              <div style={{ fontSize: 13, color: '#555', marginBottom: 10, lineHeight: 1.5 }}>{desc.summary}</div>
                              <Divider style={{ margin: '8px 0' }} />
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Page Access</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {desc.capabilities.map(cap => (
                                  <div key={cap.page} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span style={{ fontSize: 12, color: '#333' }}>
                                      {cap.perm === 'write'
                                        ? <UnlockFilled style={{ color: '#52c41a', marginRight: 5, fontSize: 11 }} />
                                        : <LockFilled style={{ color: '#faad14', marginRight: 5, fontSize: 11 }} />}
                                      {cap.page}
                                    </span>
                                    <Tag
                                      color={cap.perm === 'write' ? 'blue' : 'default'}
                                      style={{ margin: 0, fontSize: 10, padding: '0 5px' }}
                                    >
                                      {cap.perm === 'write' ? 'Read / Write' : 'Read Only'}
                                    </Tag>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                          return (
                            <Popover
                              content={popContent}
                              title={<span style={{ fontWeight: 600 }}>{roleInfo.label} — Permissions</span>}
                              trigger="click"
                              placement="rightTop"
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14 }} />}
                                style={{ padding: '0 2px', height: 'auto', lineHeight: 1 }}
                              />
                            </Popover>
                          );
                        }}
                      </Form.Item>
                    </span>
                  }
                  style={{ marginBottom: 12 }}
                  rules={[{ required: true, message: 'Please select a role' }]}
                >
                  <Select
                    placeholder="Select Admin Role"
                    size="large"
                    loading={rolesLoading}
                    suffixIcon={<SafetyOutlined style={{ color: '#bfbfbf' }} />}
                    onChange={handleRoleChange}
                  >
                    {roleOptions.map(role => (
                      <Option key={role.value} value={role.value}>
                        <Tag color={role.color} style={{ marginRight: '6px' }}>{role.label}</Tag>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Role description card — shown once a role is selected */}
                <Form.Item noStyle shouldUpdate={(p, c) => p.role !== c.role}>
                  {({ getFieldValue }) => {
                    const roleId = getFieldValue('role');
                    const roleInfo = roleOptions.find(r => r.value === roleId);
                    const descKey = roleInfo ? Object.keys(ROLE_DESCRIPTIONS).find(k => k.toLowerCase() === roleInfo.label.toLowerCase()) : null;
                    const desc = descKey ? ROLE_DESCRIPTIONS[descKey] : null;
                    if (!roleInfo) return null;
                    return (
                      <div style={{
                        background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%)',
                        border: '1px solid #91caff',
                        borderRadius: 10,
                        padding: '10px 14px',
                        marginBottom: 14,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: desc ? 6 : 0 }}>
                          <Tag color={roleInfo.color} style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>{roleInfo.label}</Tag>
                          <span style={{ fontSize: 11, color: '#1677ff', fontWeight: 500 }}>
                            {selectedPages.length} page{selectedPages.length !== 1 ? 's' : ''} assigned
                          </span>
                        </div>
                        {desc && (
                          <p style={{ fontSize: 12, color: '#4a6fa5', margin: 0, lineHeight: 1.5 }}>{desc.summary}</p>
                        )}
                        {!desc && (
                          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Default permissions applied for this role.</p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Advanced Access toggle */}
                <div style={{ marginBottom: showAdvancedAccess ? 10 : 0 }}>
                  <Button
                    type="dashed"
                    size="small"
                    icon={showAdvancedAccess ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => setShowAdvancedAccess(v => !v)}
                    style={{
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#595959',
                      borderColor: '#d9d9d9',
                      width: '100%',
                      marginBottom: showAdvancedAccess ? 10 : 0,
                    }}
                  >
                    {showAdvancedAccess ? 'Hide Advanced Access' : 'Advanced Access'}
                  </Button>

                  {/* Page Access — hidden until Advanced Access is toggled */}
                  {showAdvancedAccess && (
                    <Form.Item label={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#434343' }}>Customize Page Permissions</span>
                        <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                          onClick={() => {
                            if (selectedPages.length === AVAILABLE_PAGES.length) {
                              setSelectedPages([]);
                              setPagePermissions({});
                            } else {
                              const allKeys = AVAILABLE_PAGES.map(p => p.key);
                              setSelectedPages(allKeys);
                              const perms = {};
                              allKeys.forEach(k => { perms[k] = pagePermissions[k] || 'read'; });
                              setPagePermissions(perms);
                            }
                          }}
                        >
                          {selectedPages.length === AVAILABLE_PAGES.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                    } style={{ marginBottom: 0 }}>
                      <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
                        {Object.entries(pageGroups).map(([groupName, pages]) => (
                          <div key={groupName}>
                            <div style={{ background: '#f5f5f5', padding: '6px 12px', borderBottom: '1px solid #e8e8e8', borderTop: '1px solid #e8e8e8' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {PAGE_GROUP_LABELS[groupName] || groupName}
                              </span>
                            </div>
                            {pages.map((page, idx) => (
                              <div
                                key={page.key}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '5px 12px',
                                  background: selectedPages.includes(page.key) ? '#e6f4ff' : '#fff',
                                  borderBottom: idx < pages.length - 1 ? '1px solid #f0f0f0' : 'none',
                                  transition: 'background 0.15s',
                                }}
                              >
                                <Checkbox
                                  checked={selectedPages.includes(page.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPages(prev => [...prev, page.key]);
                                      setPagePermissions(prev => ({ ...prev, [page.key]: prev[page.key] || 'read' }));
                                    } else {
                                      setSelectedPages(prev => prev.filter(k => k !== page.key));
                                      setPagePermissions(prev => { const c = { ...prev }; delete c[page.key]; return c; });
                                    }
                                  }}
                                >
                                  <span style={{ fontSize: '12px', fontWeight: selectedPages.includes(page.key) ? 600 : 400 }}>{page.label}</span>
                                </Checkbox>
                                {selectedPages.includes(page.key) && (
                                  <Select
                                    size="small"
                                    value={pagePermissions[page.key] || 'read'}
                                    onChange={(val) => setPagePermissions(prev => ({ ...prev, [page.key]: val }))}
                                    style={{ width: 95, flexShrink: 0 }}
                                    popupMatchSelectWidth={false}
                                  >
                                    <Option value="read"><Tag color="default" style={{ margin: 0, fontSize: '10px' }}>Read</Tag></Option>
                                    <Option value="write"><Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>R/W</Tag></Option>
                                  </Select>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </Form.Item>
                  )}
                </div>
              </Col>
            </Row>

            {/* Footer buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={handleCloseCreateModal} size="large" style={{ borderRadius: '8px' }}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<UserAddOutlined />}
                size="large"
                style={{ fontWeight: 600, borderRadius: '8px', minWidth: '160px' }}
              >
                Create Admin User
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      {/* Credentials Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {createdCredentials?.restored
              ? <RollbackOutlined style={{ color: '#722ed1', fontSize: windowWidth < 576 ? '20px' : '24px' }} />
              : <CheckCircleOutlined style={{ color: '#52c41a', fontSize: windowWidth < 576 ? '20px' : '24px' }} />
            }
            <span style={{ fontSize: windowWidth < 576 ? '14px' : '18px', fontWeight: 600 }}>
              {createdCredentials?.restored ? 'Admin User Restored Successfully!' : 'Admin User Created Successfully!'}
            </span>
            {createdCredentials?.restored && (
              <Tag color="purple" style={{ fontSize: 11, marginLeft: 4 }}>Restored</Tag>
            )}
          </div>
        }
        open={showCredentialsModal}
        onCancel={() => setShowCredentialsModal(false)}
        footer={[
          <Button
            key="copy-all"
            type="primary"
            icon={<CopyOutlined />}
            block={windowWidth < 576}
            onClick={() => {
              const credentials = `
Admin User Credentials
----------------------
Name: ${createdCredentials?.name}
Username: ${createdCredentials?.username}
Password: ${createdCredentials?.password}
Role: ${createdCredentials?.rolename}

Please share these credentials securely with the user.
              `.trim();
              copyToClipboard(credentials, 'All credentials');
            }}
            style={{ marginBottom: windowWidth < 576 ? '8px' : '0' }}
          >
            Copy All
          </Button>,
          <Button
            key="close"
            onClick={() => setShowCredentialsModal(false)}
            block={windowWidth < 576}
          >
            Close
          </Button>
        ]}
        width={windowWidth < 576 ? '95%' : windowWidth < 768 ? 450 : 500}
        centered
        style={{ top: windowWidth < 576 ? 20 : undefined }}
      >
        <Alert
          type="warning"
          showIcon
          message="Important: Save these credentials!"
          description="Please copy and securely share these credentials with the user. The password cannot be recovered once this dialog is closed."
          style={{ marginBottom: '20px' }}
        />

        <div style={{ 
          background: '#f5f5f5', 
          borderRadius: '12px', 
          padding: windowWidth < 576 ? '16px' : '20px',
          border: '1px solid #e8e8e8'
        }}>
          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Full Name
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: windowWidth < 576 ? '14px' : '16px' }}>{createdCredentials?.name}</Text>
            </div>
          </div>

          {/* Username */}
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Username (Login ID)
            </Text>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: '#fff',
              padding: windowWidth < 576 ? '10px 12px' : '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              gap: '8px'
            }}>
              <Text strong style={{ fontSize: windowWidth < 576 ? '13px' : '15px', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                {createdCredentials?.username}
              </Text>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(createdCredentials?.username, 'Username')}
                size="small"
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Password
            </Text>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: '#fff',
              padding: windowWidth < 576 ? '10px 12px' : '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              gap: '8px'
            }}>
              <Text strong style={{ fontSize: windowWidth < 576 ? '13px' : '15px', fontFamily: 'monospace', color: '#1890ff', wordBreak: 'break-all', flex: 1 }}>
                {createdCredentials?.password}
              </Text>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(createdCredentials?.password, 'Password')}
                size="small"
              />
            </div>
          </div>

          {/* Role */}
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Assigned Role
            </Text>
            <Tag 
              color={roleColorMap[createdCredentials?.rolename] || 'default'} 
              style={{ fontSize: windowWidth < 576 ? '13px' : '14px', padding: windowWidth < 576 ? '2px 10px' : '4px 12px' }}
            >
              {createdCredentials?.rolename}
            </Tag>
          </div>

          {/* Assigned Pages */}
          {createdCredentials?.pages && createdCredentials.pages.length > 0 && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Assigned Pages
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {createdCredentials.pages.map(p => {
                  const perm = createdCredentials.permissions?.[p];
                  return (
                    <Tag key={p} color={perm === 'write' ? 'blue' : 'default'}>
                      {getPageLabel(p)}
                      {perm === 'write' ? ' (R/W)' : ' (R)'}
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '16px', padding: windowWidth < 576 ? '10px' : '12px', background: '#e6f7ff', borderRadius: '8px' }}>
          <Text style={{ fontSize: windowWidth < 576 ? '12px' : '13px', color: '#1890ff' }}>
            <KeyOutlined style={{ marginRight: '8px' }} />
            The user can log in using the username and password above. It's recommended that they change their password after first login.
          </Text>
        </div>
      </Modal>

      {/* Edit Permissions Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EditOutlined style={{ color: '#1890ff' }} />
            <span>Edit Permissions — {editPermUser?.name || editPermUser?.email || 'User'}</span>
          </div>
        }
        open={!!editPermUser}
        onCancel={() => {
          setEditPermUser(null);
          setEditPermPages([]);
          setEditPermPerms({});
          setEditPermRole(null);
        }}
        onOk={handleSavePermissions}
        confirmLoading={editPermLoading}
        okText="Save Permissions"
        width={windowWidth < 576 ? '92%' : windowWidth < 992 ? 600 : 700}
        bodyStyle={{ overflow: 'visible', padding: windowWidth < 576 ? '12px 14px 6px' : '14px 18px 8px' }}
        wrapClassName="admin-user-details-modal"
        style={{ top: windowWidth < 576 ? 18 : 30 }}
        destroyOnClose
      >
        {editPermUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Current user info */}
            <div style={{ background: '#f6f6f6', padding: '10px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${roleOptions.find(r => r.value === editPermUser.role)?.color || '#1890ff'} 0%, #1890ff 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '16px'
              }}>
                {editPermUser.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{editPermUser.name || '—'}</div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{editPermUser.email || editPermUser.mobile || ''}</Text>
              </div>
            </div>

            {/* Role selector */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>Role</Text>
              <Select
                value={editPermRole}
                onChange={handleEditPermRoleChange}
                style={{ width: '100%' }}
                placeholder="Select role"
                loading={rolesLoading}
                size="middle"
              >
                {roleOptions.map(role => (
                  <Option key={role.value} value={role.value}>
                    {role.label}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Pages & Permissions — 2-column grid */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Text strong style={{ fontSize: '13px' }}>Page Access</Text>
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    if (editPermPages.length === AVAILABLE_PAGES.length) {
                      setEditPermPages([]);
                      setEditPermPerms({});
                    } else {
                      const allKeys = AVAILABLE_PAGES.map(p => p.key);
                      setEditPermPages(allKeys);
                      const perms = {};
                      allKeys.forEach(k => { perms[k] = editPermPerms[k] || 'read'; });
                      setEditPermPerms(perms);
                    }
                  }}
                >
                  {editPermPages.length === AVAILABLE_PAGES.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
                {Object.entries(pageGroups).map(([groupName, pages]) => (
                  <div key={groupName}>
                    <div style={{ background: '#f5f5f5', padding: '6px 12px', borderBottom: '1px solid #e8e8e8', borderTop: '1px solid #e8e8e8' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {PAGE_GROUP_LABELS[groupName] || groupName}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: windowWidth < 576 ? '1fr' : '1fr 1fr', gap: '1px', background: '#e8e8e8' }}>
                      {pages.map(page => {
                        const isChecked = editPermPages.includes(page.key);
                        return (
                          <div
                            key={page.key}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: isChecked ? '#e6f4ff' : '#fff',
                              gap: '8px',
                              transition: 'background 0.15s',
                            }}
                          >
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditPermPages(prev => [...prev, page.key]);
                                  setEditPermPerms(prev => ({ ...prev, [page.key]: prev[page.key] || 'read' }));
                                } else {
                                  setEditPermPages(prev => prev.filter(k => k !== page.key));
                                  setEditPermPerms(prev => {
                                    const copy = { ...prev };
                                    delete copy[page.key];
                                    return copy;
                                  });
                                }
                              }}
                              style={{ fontSize: '13px' }}
                            >
                              <span style={{ fontSize: '12px', fontWeight: isChecked ? 600 : 400 }}>{page.label}</span>
                            </Checkbox>
                            {isChecked && (
                              <Select
                                size="small"
                                value={editPermPerms[page.key] || 'read'}
                                onChange={(val) => setEditPermPerms(prev => ({ ...prev, [page.key]: val }))}
                                style={{ width: 85, flexShrink: 0 }}
                                popupMatchSelectWidth={false}
                              >
                                <Option value="read"><Tag color="default" style={{ margin: 0, fontSize: '10px' }}>Read</Tag></Option>
                                <Option value="write"><Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>R/W</Tag></Option>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Restore Deactivated User Modal ─────────────────────────────── */}
      <Modal
        open={restoreModal.visible}
        onCancel={() => !restoreModal.loading && setRestoreModal({ visible: false, users: [], pendingFormData: null, loading: false })}
        footer={null}
        width={480}
        centered
        closable={!restoreModal.loading}
        maskClosable={!restoreModal.loading}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #fff7e6 0%, #fff1b8 100%)',
          borderBottom: '1px solid #ffe58f',
          padding: '24px 24px 20px',
          borderRadius: '8px 8px 0 0',
          display: 'flex', alignItems: 'flex-start', gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #fa8c16, #faad14)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, color: '#fff', flexShrink: 0, boxShadow: '0 4px 12px rgba(250,140,22,0.3)',
          }}>
            <HistoryOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#222', marginBottom: 4 }}>Previously Deactivated User Found</div>
            <div style={{ fontSize: 13, color: '#8c6914', lineHeight: 1.5 }}>
              A deactivated account with the same credentials was found.<br />
              You can restore it or use different credentials.
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {restoreModal.users.map((user, idx) => (
            <div key={idx} style={{
              border: '1px solid #e8e8e8',
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: idx < restoreModal.users.length - 1 ? 12 : 0,
            }}>
              {/* User card header */}
              <div style={{
                background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%)',
                padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 12,
                borderBottom: '1px solid #e8e8e8',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #722ed1, #531dab)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0,
                }}>
                  {(user.companyname || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#222', marginBottom: 1 }}>{user.companyname}</div>
                  <Tag color="orange" style={{ marginTop: 2, fontSize: 11 }}>Deactivated</Tag>
                </div>
              </div>
              {/* User details */}
              <div style={{ padding: '12px 16px', background: '#fff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 0' }}>
                  {user.emailid && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MailOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                      <Text style={{ fontSize: 12 }} copyable={{ text: user.emailid }}>{user.emailid}</Text>
                    </div>
                  )}
                  {user.contactno && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                      <Text style={{ fontSize: 12 }}>{user.countrycode ? `+${user.countrycode.replace('+','')}` : ''}{user.contactno}</Text>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SafetyOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                    <Text style={{ fontSize: 12 }}>{user.company_type || user.rolename || '—'}</Text>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* What will change */}
          {restoreModal.pendingFormData && (
            <div style={{
              marginTop: 16, padding: '12px 14px',
              background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: '#237804', lineHeight: 1.7 }}>
                <strong>Restoring will update: </strong>
                name to <strong>&ldquo;{restoreModal.pendingFormData.companyname}&rdquo;</strong>,
                set a new password, update role &amp; page permissions.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          background: '#fafafa', borderRadius: '0 0 8px 8px',
        }}>
          <Button
            onClick={() => setRestoreModal({ visible: false, users: [], pendingFormData: null, loading: false })}
            disabled={restoreModal.loading}
            size="large"
          >
            Cancel — Use Different Credentials
          </Button>
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            loading={restoreModal.loading}
            onClick={handleRestore}
            size="large"
            style={{ background: 'linear-gradient(135deg, #722ed1, #531dab)', border: 'none' }}
          >
            Restore This User
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminUserManagement;
